import { useEffect } from 'react';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonical?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  schema?: object;
}

export function useSEO({
  title,
  description,
  keywords,
  canonical,
  ogTitle,
  ogDescription,
  ogImage,
  schema
}: SEOProps) {
  useEffect(() => {
    // Update title
    if (title) {
      document.title = title;
    }

    // Update meta tags
    const updateMetaTag = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      
      element.setAttribute('content', content);
    };

    if (description) {
      updateMetaTag('description', description);
      if (!ogDescription) {
        updateMetaTag('og:description', description, true);
        updateMetaTag('twitter:description', description, true);
      }
    }

    if (keywords) {
      updateMetaTag('keywords', keywords);
    }

    if (ogTitle) {
      updateMetaTag('og:title', ogTitle, true);
      updateMetaTag('twitter:title', ogTitle, true);
    }

    if (ogDescription) {
      updateMetaTag('og:description', ogDescription, true);
      updateMetaTag('twitter:description', ogDescription, true);
    }

    if (ogImage) {
      updateMetaTag('og:image', ogImage, true);
      updateMetaTag('twitter:image', ogImage, true);
    }

    // Update canonical
    if (canonical) {
      let link = document.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        document.head.appendChild(link);
      }
      link.setAttribute('href', canonical);
    }

    // Update Schema.org JSON-LD
    if (schema) {
      const scriptId = 'schema-org-jsonld';
      let script = document.getElementById(scriptId);
      
      if (!script) {
        script = document.createElement('script');
        script.id = scriptId;
        script.type = 'application/ld+json';
        document.head.appendChild(script);
      }
      
      script.textContent = JSON.stringify(schema);
    }

    // Update last-modified
    const today = new Date().toISOString().split('T')[0];
    updateMetaTag('last-modified', today);
  }, [title, description, keywords, canonical, ogTitle, ogDescription, ogImage, schema]);
}

export function generateLocalBusinessSchema(siteUrl: string) {
  return {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    '@id': `${siteUrl}/#organization`,
    name: 'Green Choice Bag',
    image: `${siteUrl}/logo.jpg`,
    description: 'Premium wholesale eco-friendly paper bags supplier in City of Industry, CA. 100% recycled kraft paper bags with custom printing options.',
    url: siteUrl,
    telephone: '626-346-8383',
    email: 'hello@greenchoicebag.com',
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'City of Industry',
      addressRegion: 'CA',
      addressCountry: 'US'
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: 34.0195,
      longitude: -117.9590
    },
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
        opens: '08:00',
        closes: '18:00'
      },
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: 'Saturday',
        opens: '09:00',
        closes: '16:00'
      }
    ],
    priceRange: '$$',
    areaServed: {
      '@type': 'Country',
      name: 'United States'
    }
  };
}

export function generateProductSchema(siteUrl: string) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: 'Eco-Friendly Paper Grocery Bags',
    image: `${siteUrl}/product-image.jpg`,
    description: '100% recycled kraft paper grocery bags with spiral twisted paper handles. Available in multiple sizes (#14, #12, #10). FSC certified, biodegradable, and made in USA.',
    brand: {
      '@type': 'Brand',
      name: 'Green Choice Bag'
    },
    offers: {
      '@type': 'AggregateOffer',
      priceCurrency: 'USD',
      availability: 'https://schema.org/InStock',
      seller: {
        '@type': 'Organization',
        name: 'Green Choice Bag'
      }
    },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.9',
      reviewCount: '127'
    },
    material: 'Recycled Kraft Paper',
    additionalProperty: [
      {
        '@type': 'PropertyValue',
        name: 'Handle Type',
        value: 'Spiral twisted paper handles'
      },
      {
        '@type': 'PropertyValue',
        name: 'Quantity per Carton',
        value: '250 pieces'
      },
      {
        '@type': 'PropertyValue',
        name: 'Certification',
        value: 'FSC Certified'
      }
    ]
  };
}

export function generateWebPageSchema(siteUrl: string) {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': `${siteUrl}/#webpage`,
    url: siteUrl,
    name: 'City of Industry Wholesale Paper Bags | Green Choice Bag',
    description: 'Premium wholesale eco-friendly paper bags in City of Industry, CA. 100% recycled kraft paper bags with custom printing. FSC certified, competitive pricing, fast shipping nationwide.',
    isPartOf: {
      '@type': 'WebSite',
      '@id': `${siteUrl}/#website`,
      url: siteUrl,
      name: 'Green Choice Bag'
    },
    about: {
      '@type': 'Thing',
      name: 'Wholesale Paper Bags'
    },
    primaryImageOfPage: {
      '@type': 'ImageObject',
      url: `${siteUrl}/hero-image.jpg`
    }
  };
}

export function generateFAQSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'What sizes of paper bags do you offer?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'We offer three standard sizes: #14 (14 x 10 x 15.5), #12 (12.85 x 7 x 13), and #10 (10 x 6.75 x 12). All sizes come with spiral twisted paper handles.'
        }
      },
      {
        '@type': 'Question',
        name: 'Are your paper bags eco-friendly?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Yes, our paper bags are made from 100% recycled kraft paper, are FSC certified, biodegradable, and completely recyclable. They are an environmentally responsible alternative to plastic bags.'
        }
      },
      {
        '@type': 'Question',
        name: 'What is the minimum order quantity?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Our bags are sold in cartons of 250 pieces. We offer flexible quantity options for wholesale orders to meet your business needs.'
        }
      },
      {
        '@type': 'Question',
        name: 'Do you offer custom printing?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Yes, we offer custom printing services. You can add your brand logo and design to create unique packaging that represents your business.'
        }
      },
      {
        '@type': 'Question',
        name: 'How long does shipping take?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'We offer fast shipping nationwide. Delivery times vary based on your location, but we prioritize quick turnaround to meet your business needs.'
        }
      }
    ]
  };
}

export function generateBreadcrumbSchema(siteUrl: string) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: siteUrl
      }
    ]
  };
}
